import { useState, useCallback } from "react";
import { toast } from "@/hooks/use-toast";
import { useCreateOrder } from "@/services/order-service";
import { getCustomer } from "@/api/customer-api";
import { QueueCustomer, Participant } from "@/types/fb-type";
import { OrderCreateFormValues } from "@/types/order-type";
import { Customer } from "@/types/customer-type";

export function useFloatingOrder() {
  const [floatingInitialData, setFloatingInitialData] = useState<
    Partial<OrderCreateFormValues> | undefined
  >();
  const createOrderMutation = useCreateOrder();
  const [floatingCustomer, setFloatingCustomer] = useState<
    Customer | undefined
  >();
  const [floatingOpen, setFloatingOpen] = useState(false);

  const handleProductDropOnQueue = useCallback(
    async (qCustomer: QueueCustomer, productId: string, variantId: string) => {
      const customerResponse = await getCustomer(qCustomer.customerId);
      if (!customerResponse.success) {
        toast({
          title: "Error",
          description:
            customerResponse.message ||
            "Unexpected error for creating customer",
          variant: "destructive",
        });
        return;
      }

      setFloatingCustomer(customerResponse.data);
      const customerId = customerResponse.data.id;
      setFloatingInitialData((prev) => {
        const existingItems = prev?.items || [];
        const newItems = [...existingItems, { variantId, quantity: 1 }];
        return {
          ...prev,
          customerId: customerId,
          items: newItems,
        };
      });
      setFloatingOpen(true);
    },
    []
  );

  const handleConfirmFloatingOrder = useCallback(
    async (values: OrderCreateFormValues) => {
      const response = await createOrderMutation.executeAsync(values);
      try {
        if (response.success) {
          setFloatingOpen(false);
          setFloatingCustomer(undefined);
          setFloatingInitialData(undefined);
        } else {
          toast({
            title: "Error",
            description:
              response.message || "Unexpected error for creating order",
            variant: "destructive",
          });
        }
      } catch (error) {
        const msg =
          error instanceof Error
            ? error.message
            : "Unexpected error for creating order";
        toast({ title: "Error", description: msg, variant: "destructive" });
      }
    },
    [createOrderMutation]
  );

  const handleCloseFloating = useCallback(() => {
    setFloatingOpen(false);
    setFloatingCustomer(undefined);
    setFloatingInitialData(undefined);
  }, []);

  return {
    floatingCustomer,
    floatingOpen,
    floatingInitialData,
    handleProductDropOnQueue,
    handleConfirmFloatingOrder,
    handleCloseFloating,
  };
}
